import type { FC } from 'react';
import React, { useCallback, Fragment } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ErrorDisplay,
	AtlassianSupportLink,
	withTransparentErrorBoundary,
	Attribution,
} from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';
import type { FlagsStateContainer } from '@confluence/flags';
import { withFlags } from '@confluence/flags';

import { fireUIClickEvent } from '../AnalyticsEvents';
import { CreateOrUpdateArchivePageNoteMutation } from '../CreateOrUpdateArchivePageNoteMutation.graphql';
import type {
	CreateOrUpdateArchivePageNoteMutation as CreateOrUpdateArchivePageNoteMutationType,
	CreateOrUpdateArchivePageNoteMutationVariables,
} from '../__types__/CreateOrUpdateArchivePageNoteMutation';
import { ArchivedPageBannerQuery } from '../ArchivedPageBannerQuery.graphql';
import type {
	ArchivedPageBannerQuery as ArchivedPageBannerQueryType,
	ArchivedPageBannerQueryVariables,
} from '../__types__/ArchivedPageBannerQuery';

import { ArchivedPageBannerViewNote } from './ArchivedPageBannerViewNote';
import { ArchivedPageBannerEditNote } from './ArchivedPageBannerEditNote';

export const i18n = defineMessages({
	createNoteErrorTitle: {
		id: 'archive-pages.page-banner.note.creation.error.title',
		defaultMessage: 'We couldn’t add your note to the page',
		description: 'title for error flag when the action to add a note to an archived page fails',
	},
	updateNoteErrorTitle: {
		id: 'archive-pages.page-banner.note.update.error.title',
		defaultMessage: 'We couldn’t edit your note for the page',
		description:
			'title for error flag when the action to update an existing note on an archived page fails',
	},
	createNoteErrorDescription: {
		id: 'archive-pages.page-banner.note.creation.error.description',
		defaultMessage:
			'Try again. If this keeps happening, contact {supportLink} or your space admin.',
		description:
			'description for error flag when the action to add a note to an archived page fails',
	},
	createNoteSuccessTitle: {
		id: 'archive-pages.page-banner.note.creation.success.title',
		defaultMessage: 'Note added',
		description:
			'title for success flag when the action to add a note to an archived page succeeds',
	},
	updateNoteSuccessTitle: {
		id: 'archive-pages.page-banner.note.update.success.title',
		defaultMessage: 'Note edited',
		description:
			'title for success flag when the action to update an existing note on an archived page succeeds',
	},
});

type ArchivedPageBannerNoteProps = {
	note: string;
	archiverAccountId?: string | null;
	contentId: string;
	editingNote: boolean;
	setEditingNote: (editingNote: boolean) => void;
	flags: FlagsStateContainer;
};

const ArchivedPageBannerNoteComponent: FC<ArchivedPageBannerNoteProps> = ({
	note,
	archiverAccountId,
	contentId,
	editingNote,
	setEditingNote,
	flags,
}) => {
	const { userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [createOrUpdateNote, { error: noteCreationError }] = useMutation<
		CreateOrUpdateArchivePageNoteMutationType,
		CreateOrUpdateArchivePageNoteMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		CreateOrUpdateArchivePageNoteMutation,
		{
			update: (cache, { data: responseData }) => {
				const cachedData = cache.readQuery<
					ArchivedPageBannerQueryType,
					ArchivedPageBannerQueryVariables
				>({
					query: ArchivedPageBannerQuery,
					variables: { contentId },
				});
				if (cachedData && responseData) {
					const contentNode = cachedData.content?.nodes?.[0];
					if (contentNode) {
						contentNode.archivedContentMetadata = {
							...contentNode.archivedContentMetadata,
							archiveNote: responseData.createOrUpdateArchivePageNote,
						};

						cache.writeQuery<ArchivedPageBannerQueryType, ArchivedPageBannerQueryVariables>({
							query: ArchivedPageBannerQuery,
							variables: { contentId },
							data: cachedData,
						});
					}
				}
			},
			onError: () => {
				void flags.showErrorFlag({
					title: (
						<FormattedMessage {...(note ? i18n.updateNoteErrorTitle : i18n.createNoteErrorTitle)} />
					),
					description: (
						<FormattedMessage
							{...i18n.createNoteErrorDescription}
							values={{ supportLink: <AtlassianSupportLink /> }}
						/>
					),
				});
			},
		},
	);

	const userCanUpdateNote = userId === archiverAccountId;

	const openNoteCreation = useCallback(() => {
		setEditingNote(true);
	}, [setEditingNote]);

	const closeNoteCreation = useCallback(() => {
		setEditingNote(false);
	}, [setEditingNote]);

	const handleSaveNote = useCallback(
		(newArchiveNote: string) => {
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'button',
				actionSubjectId: !note ? 'addArchiveNoteSubmitButton' : 'updateArchiveNoteSubmitButton',
				source: 'archivePageBanner',
				attributes: {
					includedNote: !!newArchiveNote,
				},
			});

			void createOrUpdateNote({
				variables: {
					pageId: contentId,
					archiveNote: newArchiveNote,
				},
				optimisticResponse: {
					createOrUpdateArchivePageNote: newArchiveNote,
				},
			});

			closeNoteCreation();
		},
		[contentId, closeNoteCreation, createOrUpdateNote, createAnalyticsEvent, note],
	);

	const renderNote = () => {
		if (editingNote) {
			return (
				<ArchivedPageBannerEditNote
					defaultValue={note}
					onSave={handleSaveNote}
					onCancel={closeNoteCreation}
				/>
			);
		}

		return (
			<ArchivedPageBannerViewNote
				note={note}
				userCanUpdateNote={userCanUpdateNote}
				openNoteCreation={openNoteCreation}
			/>
		);
	};

	return (
		<Fragment>
			{renderNote()}
			{noteCreationError && <ErrorDisplay error={noteCreationError} />}
		</Fragment>
	);
};

export const ArchivedPageBannerNote = withTransparentErrorBoundary({
	attribution: Attribution.ADMIN_EXPERIENCE,
})(withFlags(ArchivedPageBannerNoteComponent));
